import { useState, useEffect } from 'react';
import { supplierReconAPI, supplierReconSessionsAPI } from '../lib/api';
import { FiX, FiPackage, FiCalendar, FiCloud, FiMail, FiDatabase, FiLoader, FiAlertCircle, FiAlertTriangle, FiChevronDown } from 'react-icons/fi';

const DOC_TYPE_LABELS = {
  supplier_invoices: 'Supplier Invoices',
  supplier_statements: 'Supplier Statements',
  company_supplier_ledgers: 'Company Supplier Ledgers',
  supplier_payments: 'Supplier Payments',
  payment_files: 'Payment Files',
  purchase_listing: 'Purchase Listings',
  supplier_payout: 'Payouts',
  pos_cashup: 'Cashups',
  pos_day_end: 'POS Day End Reports',
  purchase_order: 'Purchase Orders',
};

function defaultSessionName(groupName) {
  const now = new Date();
  const month = now.toLocaleString('default', { month: 'long' });
  const year = now.getFullYear();
  return groupName ? `${groupName} – ${month} ${year} Recon` : `${month} ${year} Recon`;
}

function toLocalDateStr(d) {
  // Use local year/month/day to avoid UTC timezone shift (e.g. SAST UTC+2 would shift midnight to previous day)
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function defaultPeriod() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const start = new Date(year, month, 1);
  const end = new Date(year, month + 1, 0);
  return {
    start: toLocalDateStr(start),
    end: toLocalDateStr(end),
  };
}

export default function NewReconSessionModal({ onClose, onCreated }) {
  const period = defaultPeriod();
  const [periodStart, setPeriodStart] = useState(period.start);
  const [periodEnd, setPeriodEnd] = useState(period.end);
  const [name, setName] = useState(defaultSessionName());
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(null); // null | 'creating' | 'syncing' | 'done' | 'warn' | 'date-warn'
  const [stepDetail, setStepDetail] = useState('');
  const [pendingSession, setPendingSession] = useState(null);
  const [fileDateRange, setFileDateRange] = useState(null);

  useEffect(() => {
    supplierReconAPI.getGroups()
      .then(res => {
        const g = res.data.groups || [];
        setGroups(g);
        if (g.length === 1) {
          setSelectedGroupId(g[0].id);
          setName(defaultSessionName(g[0].name));
        }
      })
      .catch(() => {})
      .finally(() => setLoadingGroups(false));
  }, []);

  const selectedGroup = groups.find(g => g.id === selectedGroupId) || null;
  const selectedGroupMappings = selectedGroup?.folder_mappings || [];

  const handleGroupChange = (e) => {
    const id = e.target.value;
    setSelectedGroupId(id);
    const g = groups.find(gr => gr.id === id);
    setName(defaultSessionName(g?.name));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    if (!periodStart || !periodEnd) return;
    if (periodStart > periodEnd) {
      setError('Period start must be before period end');
      return;
    }
    if (!selectedGroupId) {
      setError('Please select a recon group');
      return;
    }
    try {
      setSubmitting(true);
      setError(null);

      setStep('creating');
      setStepDetail('Creating session...');
      const createRes = await supplierReconSessionsAPI.createSession({
        name: name.trim(),
        period_start: periodStart,
        period_end: periodEnd,
        group_id: selectedGroupId,
      });
      const session = createRes.data.session;

      setStep('syncing');
      // Sync one mapping at a time (each call stays within Netlify's 26s timeout)
      const mappingCount = selectedGroupMappings.length || 1;
      let totalSynced = 0, lastFileDateRange = null;
      for (let i = 0; i < mappingCount; i++) {
        setStepDetail(`Syncing folder ${i + 1} of ${mappingCount}...`);
        const syncRes = await supplierReconSessionsAPI.syncSession(session.id, i, mappingCount);
        totalSynced += syncRes.data.synced || 0;
        if (syncRes.data.file_date_range) lastFileDateRange = syncRes.data.file_date_range;
        if (syncRes.data.done) break; // backend says we're done (handles edge cases)
      }
      const synced = totalSynced;
      const file_date_range = lastFileDateRange;

      if (synced === 0) {
        setStepDetail(`No documents found in the mapped folders for the selected period (${periodStart} – ${periodEnd}). Check that your folders contain files within this date range.`);
        setStep('warn');
        await new Promise(r => setTimeout(r, 3000));
        onCreated(session);
      } else if (file_date_range && (file_date_range.max < periodStart || file_date_range.min > periodEnd)) {
        setFileDateRange(file_date_range);
        setPendingSession(session);
        setStep('date-warn');
        setSubmitting(false);
      } else {
        setStepDetail(`Synced ${synced} document${synced !== 1 ? 's' : ''}. AI processing will continue in the background.`);
        setStep('done');
        onCreated(session);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setSubmitting(false);
      setStep(null);
    }
  };

  // Group by doc type for the preview
  const groupedMappings = selectedGroupMappings.reduce((acc, m) => {
    const t = m.type || 'unknown';
    if (!acc[t]) acc[t] = [];
    acc[t].push(m);
    return acc;
  }, {});

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <FiPackage className="w-5 h-5 text-green-600" />
            <h2 className="text-lg font-semibold text-gray-900">New Recon Run</h2>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 transition-colors">
            <FiX className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="px-6 py-5 space-y-5">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                <FiAlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Recon Group */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Recon Group
              </label>
              {loadingGroups ? (
                <div className="flex items-center gap-2 py-3 text-sm text-gray-500">
                  <FiLoader className="w-4 h-4 animate-spin" />
                  Loading groups...
                </div>
              ) : groups.length === 0 ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                  No recon groups configured. Add groups in System Settings → Supplier Recon before running a recon.
                </div>
              ) : (
                <div className="relative">
                  <select
                    value={selectedGroupId}
                    onChange={handleGroupChange}
                    required
                    className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent appearance-none bg-white"
                  >
                    <option value="">Select a group...</option>
                    {groups.map(g => (
                      <option key={g.id} value={g.id}>
                        {g.name}{g.description ? ` — ${g.description}` : ''} ({g.folder_mappings?.length || 0} folder{(g.folder_mappings?.length || 0) !== 1 ? 's' : ''})
                      </option>
                    ))}
                  </select>
                  <FiChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              )}

              {/* Selected group folder preview */}
              {selectedGroup && selectedGroupMappings.length > 0 && (
                <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden divide-y divide-gray-100 max-h-36 overflow-y-auto">
                  {Object.entries(groupedMappings).map(([type, mappings]) => (
                    <div key={type}>
                      <div className="px-3 py-1 bg-gray-50 text-xs font-medium text-gray-500">
                        {DOC_TYPE_LABELS[type] || type} ({mappings.length})
                      </div>
                      {mappings.map(m => (
                        <div key={m.id} className="flex items-center gap-2 px-3 py-1.5 bg-white">
                          {m.source === 'sharepoint'
                            ? <FiCloud className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
                            : m.source === 'doxolve'
                              ? <FiDatabase className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                              : <FiMail className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" />
                          }
                          <span className="text-xs text-gray-700 truncate">
                            {m.source === 'sharepoint'
                              ? m.folder_path || [m.site_name, m.drive_name, m.folder_name].filter(Boolean).join(' / ')
                              : m.source === 'doxolve'
                                ? m.folder_path || m.folder_name || 'Root'
                                : [m.mailbox_email, m.mail_folder_name].filter(Boolean).join(' / ')
                            }
                          </span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Session Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Session Name
              </label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g. Evonik – February 2026 Recon"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            {/* Period */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <span className="flex items-center gap-1.5">
                  <FiCalendar className="w-4 h-4" />
                  Recon Period
                </span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">From</label>
                  <input
                    type="date"
                    value={periodStart}
                    onChange={e => setPeriodStart(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">To</label>
                  <input
                    type="date"
                    value={periodEnd}
                    onChange={e => setPeriodEnd(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Info */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-xs text-green-800 space-y-1">
              <p className="font-medium">What happens when you start:</p>
              <ol className="list-decimal list-inside space-y-0.5 text-green-700">
                <li>All folders in the selected group are synced for the period</li>
                <li>Each document is AI-processed to extract supplier data</li>
                <li>Automated matching runs: invoices vs statements vs payments</li>
                <li>Discrepancies are flagged and a report is generated</li>
              </ol>
            </div>
          </div>

          {/* Progress indicator */}
          {step && step !== 'warn' && step !== 'date-warn' && (
            <div className="px-6 pb-2">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-green-800">
                  <FiLoader className="w-4 h-4 animate-spin flex-shrink-0" />
                  <span>
                    {step === 'creating' && 'Creating session...'}
                    {step === 'syncing' && 'Syncing documents from folders...'}
                    {step === 'done' && 'Sync complete!'}
                  </span>
                </div>
                {stepDetail && (
                  <p className="text-xs text-green-700 pl-6">{stepDetail}</p>
                )}
                <div className="h-1.5 bg-green-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-green-500 rounded-full transition-all duration-500"
                    style={{ width: step === 'creating' ? '20%' : step === 'syncing' ? '60%' : '100%' }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* No-documents warning */}
          {step === 'warn' && (
            <div className="px-6 pb-2">
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 flex items-start gap-2">
                <FiAlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-amber-800">No documents found for this date range</p>
                  <p className="text-xs text-amber-700">{stepDetail}</p>
                  <p className="text-xs text-amber-600">The session was created and you will be taken to it. You can adjust the period or check your folder mappings.</p>
                </div>
              </div>
            </div>
          )}

          {/* File-date mismatch warning */}
          {step === 'date-warn' && fileDateRange && (
            <div className="px-6 pb-2">
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 space-y-3">
                <div className="flex items-start gap-2">
                  <FiAlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-amber-800">Documents may not match your selected period</p>
                    <p className="text-xs text-amber-700">
                      The files in your mapped folders were last modified between <strong>{fileDateRange.min}</strong> and <strong>{fileDateRange.max}</strong>,
                      which is outside your selected recon period of <strong>{periodStart}</strong> to <strong>{periodEnd}</strong>.
                    </p>
                    <p className="text-xs text-amber-600">You can continue anyway (the AI will still extract document dates), or go back to adjust the period.</p>
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <button
                    type="button"
                    onClick={async () => {
                      if (pendingSession) {
                        try { await supplierReconSessionsAPI.deleteSession(pendingSession.id); } catch (_) {}
                      }
                      setStep(null); setPendingSession(null); setFileDateRange(null);
                    }}
                    className="px-3 py-1.5 text-xs text-amber-700 border border-amber-300 rounded-lg hover:bg-amber-100 transition-colors"
                  >
                    Adjust Period
                  </button>
                  <button
                    type="button"
                    onClick={() => onCreated(pendingSession)}
                    className="px-3 py-1.5 text-xs text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Continue Anyway
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          {step !== 'date-warn' && (
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !selectedGroupId || groups.length === 0}
                className="inline-flex items-center gap-2 px-5 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {submitting ? (
                  <>
                    <FiLoader className="w-4 h-4 animate-spin" />
                    Running...
                  </>
                ) : (
                  <>
                    <FiPackage className="w-4 h-4" />
                    Start Recon Run
                  </>
                )}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
